import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import '../styles/Auth.css';

function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [token] = useState(searchParams.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Simple strength indicator
  const getStrength = (pw) => {
    if (!pw) return null;
    let score = 0;
    if (pw.length >= 8)  score++;
    if (pw.length >= 12) score++;
    if (/[0-9]/.test(pw)) score++;
    if (/[^a-zA-Z0-9]/.test(pw)) score++;
    if (score <= 1) return { label:'Weak',   color:'#ef4444', width:'25%' };
    if (score === 2) return { label:'Fair',   color:'#f59e0b', width:'50%' };
    if (score === 3) return { label:'Good',   color:'#06b6d4', width:'75%' };
    return               { label:'Strong', color:'#10b981', width:'100%' };
  };

  const strength = getStrength(newPassword);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError('Missing reset token. Please restart the forgot password flow.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await api.post('/auth/password/reset', {
        reset_token: token,
        new_password: newPassword,
      });
      navigate('/login?success=Password reset successfully. You can now sign in.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      {/* ── Sidebar ── */}
      <div className="auth-sidebar">
        <Link to="/" className="auth-sidebar__logo">
          Postrify <span className="auth-sidebar__dot" />
        </Link>
        <div className="auth-sidebar__body">
          <h2 className="auth-sidebar__headline">
            Set a new<br /><span>password.</span>
          </h2>
          <p className="auth-sidebar__desc">
            Choose a strong, unique password to keep your
            Postrify account secure.
          </p>
          <ul className="auth-sidebar__features">
            {['At least 8 characters', 'Include a number', 'Use a special character', 'Avoid common passwords'].map((f) => (
              <li className="auth-sidebar__feature" key={f}>
                <span className="auth-sidebar__feature-icon">✓</span> {f}
              </li>
            ))}
          </ul>
        </div>
        <p className="auth-sidebar__footer">© 2024 Postrify</p>
      </div>

      {/* ── Form Panel ── */}
      <div className="auth-panel">
        <div className="auth-form-wrap">
          <Link to="/login" className="auth-form-wrap__back">← Back to sign in</Link>

          {/* Icon */}
          <div style={{
            width:'56px', height:'56px', borderRadius:'16px', background:'#e0e7ff',
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:'28px', marginBottom:'20px',
          }}>
            🔒
          </div>

          <h1 className="auth-form-wrap__title">Set new password</h1>
          <p className="auth-form-wrap__sub">Choose a strong password for your account.</p>

          {!token && (
            <div className="ps-alert ps-alert--warning">
              ⚠ Invalid or missing reset link. Please{' '}
              <Link to="/forgot-password" style={{ color:'#92400e', fontWeight:600 }}>request a new one</Link>.
            </div>
          )}
          {error && <div className="ps-alert ps-alert--error">{error}</div>}

          <form onSubmit={handleSubmit}>
            <div className="ps-field">
              <label className="ps-label" htmlFor="newPassword">New Password</label>
              <input className="ps-input" type="password" id="newPassword"
                placeholder="Min. 8 characters" autoComplete="new-password"
                minLength={8} required
                value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />

              {/* Strength bar */}
              {strength && (
                <div style={{ marginTop:'8px' }}>
                  <div style={{ height:'4px', background:'#e4e4ef', borderRadius:'2px', overflow:'hidden' }}>
                    <div style={{ height:'100%', width: strength.width, background: strength.color, borderRadius:'2px', transition:'all 0.3s' }} />
                  </div>
                  <p style={{ fontSize:'12px', color: strength.color, fontWeight:600, marginTop:'4px' }}>
                    {strength.label} password
                  </p>
                </div>
              )}
              <p className="ps-hint">At least 8 characters. Include a number and special character for a stronger password.</p>
            </div>

            <div className="ps-field">
              <label className="ps-label" htmlFor="confirmPassword">Confirm New Password</label>
              <input className="ps-input" type="password" id="confirmPassword"
                placeholder="Re-enter your password" autoComplete="new-password"
                required
                value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)} />
              {confirmPassword && newPassword !== confirmPassword && (
                <p style={{ fontSize:'12px', color:'#ef4444', marginTop:'4px', fontWeight:500 }}>Passwords don't match yet.</p>
              )}
              {confirmPassword && newPassword === confirmPassword && (
                <p style={{ fontSize:'12px', color:'#10b981', marginTop:'4px', fontWeight:500 }}>✓ Passwords match.</p>
              )}
            </div>

            <button type="submit" className="ps-btn ps-btn--primary ps-btn--full ps-btn--lg"
              disabled={loading || !token}>
              {loading ? 'Updating password…' : 'Reset password →'}
            </button>
          </form>

          <p className="auth-bottom">
            Remembered it? <Link to="/login">Sign in instead</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
